import type { ReactNode } from 'react';

export function Cartao({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <section
      className={`rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-6 ${className}`}
    >
      {children}
    </section>
  );
}

export function Titulo({ children, sub }: { children: ReactNode; sub?: ReactNode }) {
  return (
    <header className="mb-4">
      <h2 className="text-lg font-semibold text-slate-900">{children}</h2>
      {sub && <p className="mt-1 text-sm text-slate-600">{sub}</p>}
    </header>
  );
}

type Variante = 'primario' | 'secundario' | 'perigo' | 'fantasma';
const estilos: Record<Variante, string> = {
  primario: 'bg-marca-600 text-white hover:bg-marca-700 disabled:bg-marca-500/50',
  secundario: 'bg-white text-slate-800 border border-slate-300 hover:bg-slate-50',
  perigo: 'bg-white text-red-700 border border-red-200 hover:bg-red-50',
  fantasma: 'text-marca-700 hover:bg-marca-50',
};

export function Botao({
  children,
  variante = 'primario',
  className = '',
  carregando = false,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { variante?: Variante; carregando?: boolean }) {
  return (
    <button
      type="button"
      {...props}
      disabled={props.disabled || carregando}
      className={`inline-flex min-h-11 items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-base font-medium transition disabled:cursor-not-allowed disabled:opacity-70 ${estilos[variante]} ${className}`}
    >
      {carregando && <Spinner />}
      {children}
    </button>
  );
}

export function Spinner({ className = 'h-4 w-4' }: { className?: string }) {
  return (
    <svg className={`animate-spin ${className}`} viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" className="opacity-25" />
      <path
        d="M4 12a8 8 0 018-8"
        stroke="currentColor"
        strokeWidth="4"
        strokeLinecap="round"
        className="opacity-90"
      />
    </svg>
  );
}

export function Alerta({
  tipo = 'info',
  children,
}: {
  tipo?: 'info' | 'sucesso' | 'aviso' | 'erro';
  children: ReactNode;
}) {
  const cores = {
    info: 'border-marca-100 bg-marca-50 text-marca-900',
    sucesso: 'border-emerald-200 bg-emerald-50 text-emerald-900',
    aviso: 'border-amber-200 bg-amber-50 text-amber-900',
    erro: 'border-red-200 bg-red-50 text-red-900',
  }[tipo];
  return (
    <div className={`rounded-xl border px-4 py-3 text-sm leading-relaxed ${cores}`}>{children}</div>
  );
}

export function Campo({
  rotulo,
  ajuda,
  erro,
  children,
  htmlFor,
}: {
  rotulo: string;
  ajuda?: string;
  erro?: string;
  children: ReactNode;
  htmlFor?: string;
}) {
  return (
    <label className="block" htmlFor={htmlFor}>
      <span className="mb-1 block text-sm font-medium text-slate-700">{rotulo}</span>
      {children}
      {erro ? (
        <span className="mt-1 block text-sm text-red-700">{erro}</span>
      ) : ajuda ? (
        <span className="mt-1 block text-xs text-slate-500">{ajuda}</span>
      ) : null}
    </label>
  );
}

export function Selo({
  cor,
  children,
}: {
  cor: 'verde' | 'amarelo' | 'vermelho' | 'cinza' | 'azul';
  children: ReactNode;
}) {
  const c = {
    verde: 'bg-emerald-100 text-emerald-800',
    amarelo: 'bg-amber-100 text-amber-800',
    vermelho: 'bg-red-100 text-red-800',
    cinza: 'bg-slate-100 text-slate-700',
    azul: 'bg-marca-100 text-marca-900',
  }[cor];
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${c}`}
    >
      {children}
    </span>
  );
}
